import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Bot,
  FileBarChart2,
  Play,
  ExternalLink,
  Building2,
  Network,
  Eye,
  Lightbulb,
  MessageSquare,
  ShieldAlert,
  Cpu,
  Swords,
  Scale,
  Newspaper,
  BarChart3,
  Database,
  TrendingUp,
} from 'lucide-react';
import Sidebar from './Sidebar';
import LanguageSwitcher from './LanguageSwitcher';
import UserProfileDropdown from './UserProfileDropdown';
import { useLanguage } from '../hooks/useLanguage';

const BASE_WORKFLOW_URL = 'https://auto.supervity.ai/u/alpha/agent/workflow';

const AGENT_DEFS = [
  {
    id: 1,
    key: 'basicInformation',
    workflowId: '019e1acf-3f6a-7000-92e6-231a94793d7d',
    icon: <Building2 size={22} />,
    color: 'from-blue-500 to-blue-600',
    ring: 'ring-blue-300',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
  },
  {
    id: 2,
    key: 'businessStructure',
    workflowId: '019e1acf-7f94-7000-8a12-094cfd979d77',
    icon: <Network size={22} />,
    color: 'from-violet-500 to-violet-600',
    ring: 'ring-violet-300',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
  },
  {
    id: 3,
    key: 'visionAnalysis',
    workflowId: '019e1acf-c00b-7000-8e4d-6d223c36edbd',
    icon: <Eye size={22} />,
    color: 'from-cyan-500 to-cyan-600',
    ring: 'ring-cyan-300',
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
  },
  {
    id: 4,
    key: 'managementStrategy',
    workflowId: '019e1ad1-546e-7000-90a6-afa9d5607b12',
    icon: <Lightbulb size={22} />,
    color: 'from-amber-500 to-amber-600',
    ring: 'ring-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
  },
  {
    id: 5,
    key: 'managementMessage',
    workflowId: '019e1ad1-e702-7000-9972-a79cde14d3cb',
    icon: <MessageSquare size={22} />,
    color: 'from-emerald-500 to-emerald-600',
    ring: 'ring-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
  },
  {
    id: 6,
    key: 'crisisManagement',
    workflowId: '019e1ad2-3f1c-7000-90f6-e10bc02c7d9c',
    icon: <ShieldAlert size={22} />,
    color: 'from-red-500 to-red-600',
    ring: 'ring-red-300',
    bg: 'bg-red-50',
    text: 'text-red-700',
  },
  {
    id: 7,
    key: 'dxAnalysis',
    workflowId: '019e1ad2-76ae-7000-8cda-27dc44e7fdc4',
    icon: <Cpu size={22} />,
    color: 'from-indigo-500 to-indigo-600',
    ring: 'ring-indigo-300',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
  },
  {
    id: 8,
    key: 'competitiveLandscape',
    workflowId: '019e1ad2-ba57-7000-b53b-48ad8b916092',
    icon: <Swords size={22} />,
    color: 'from-orange-500 to-orange-600',
    ring: 'ring-orange-300',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
  },
  {
    id: 9,
    key: 'regulatoryEnvironment',
    workflowId: '019e1ad3-2d0e-7000-bac1-4a31968ee75d',
    icon: <Scale size={22} />,
    color: 'from-teal-500 to-teal-600',
    ring: 'ring-teal-300',
    bg: 'bg-teal-50',
    text: 'text-teal-700',
  },
  {
    id: 10,
    key: 'latestNews',
    workflowId: '019e1ad3-7e76-7000-860d-132f76e15915',
    icon: <Newspaper size={22} />,
    color: 'from-pink-500 to-pink-600',
    ring: 'ring-pink-300',
    bg: 'bg-pink-50',
    text: 'text-pink-700',
  },
  {
    id: 11,
    key: 'financialAnalysis',
    workflowId: '019e2136-2a14-7000-b751-118a288baf49',
    icon: <BarChart3 size={22} />,
    color: 'from-green-600 to-green-700',
    ring: 'ring-green-300',
    bg: 'bg-green-50',
    text: 'text-green-700',
  },
  {
    id: 12,
    key: 'nesicProprietary',
    workflowId: '019e2136-0049-7000-b29f-4e782afd35a3',
    icon: <Database size={22} />,
    color: 'from-purple-600 to-purple-700',
    ring: 'ring-purple-300',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
  },
  {
    id: 13,
    key: 'threeYearStrategy',
    workflowId: '019e2135-cd70-7000-bfea-1472974896c2',
    icon: <TrendingUp size={22} />,
    color: 'from-rose-600 to-rose-700',
    ring: 'ring-rose-300',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
  },
];

const openAgent = (workflowId) => {
  window.open(`${BASE_WORKFLOW_URL}/${workflowId}?tab=Workflow`, '_blank', 'noopener,noreferrer');
};

/* ─── SVG Arrow connector drawn between two DOM elements ─── */
const FlowConnector = ({ fromRef, toRef, containerRef, color = '#6366f1', dashed = false }) => {
  const [path, setPath] = useState('');

  useEffect(() => {
    const calculate = () => {
      if (!fromRef.current || !toRef.current || !containerRef.current) return;
      const container = containerRef.current.getBoundingClientRect();
      const from = fromRef.current.getBoundingClientRect();
      const to = toRef.current.getBoundingClientRect();

      const x1 = from.left + from.width / 2 - container.left;
      const y1 = from.bottom - container.top;
      const x2 = to.left + to.width / 2 - container.left;
      const y2 = to.top - container.top;

      const cy = (y1 + y2) / 2;
      setPath(`M${x1},${y1} C${x1},${cy} ${x2},${cy} ${x2},${y2}`);
    };

    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, [fromRef, toRef, containerRef]);

  if (!path) return null;

  return (
    <path
      d={path}
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeDasharray={dashed ? '5 4' : undefined}
      markerEnd="url(#arrowhead)"
      opacity="0.55"
    />
  );
};

const AgentsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const startRef = useRef(null);
  const reportRef = useRef(null);
  const agentRefs = useRef(AGENT_DEFS.map(() => React.createRef()));
  const [, forceUpdate] = useState(0);
  const [currentLanguage, setLanguage] = useLanguage();

  const agentName = (key) => t(`agents.items.${key}.name`);
  const agentDescription = (key) => t(`agents.items.${key}.description`);

  // Force recalculate arrows after mount + layout
  useEffect(() => {
    const t1 = setTimeout(() => forceUpdate((n) => n + 1), 100);
    const t2 = setTimeout(() => forceUpdate((n) => n + 1), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentLanguage]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboards')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              id="agents-back-btn"
            >
              <ArrowLeft size={16} />
              {t('agents.backToDashboards')}
            </button>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-indigo-600" />
              <span className="font-semibold text-gray-800">{t('agents.headerTitle')}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher
              currentLanguage={currentLanguage}
              onLanguageChange={setLanguage}
              isResearchDashboard={false}
            />
            <UserProfileDropdown />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('agents.pageTitle')}</h1>
              <p className="text-gray-500 max-w-lg mx-auto text-sm">
                {t('agents.pageDescription')}
              </p>
            </div>

            <div className="relative" ref={containerRef}>
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ overflow: 'visible', zIndex: 0 }}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="8"
                    markerHeight="8"
                    refX="4"
                    refY="4"
                    orient="auto"
                  >
                    <path d="M0,0 L8,4 L0,8 L2,4 Z" fill="#6366f1" opacity="0.5" />
                  </marker>
                </defs>

                {AGENT_DEFS.map((_, i) => (
                  <FlowConnector
                    key={`start-${i}`}
                    fromRef={startRef}
                    toRef={agentRefs.current[i]}
                    containerRef={containerRef}
                    color="#6366f1"
                    dashed={false}
                  />
                ))}

                {AGENT_DEFS.map((_, i) => (
                  <FlowConnector
                    key={`end-${i}`}
                    fromRef={agentRefs.current[i]}
                    toRef={reportRef}
                    containerRef={containerRef}
                    color="#10b981"
                    dashed={true}
                  />
                ))}
              </svg>

              <div className="flex justify-center mb-10 relative z-10">
                <div
                  ref={startRef}
                  className="
                    flex items-center gap-3 px-7 py-3.5
                    bg-gradient-to-r from-indigo-600 to-indigo-500
                    text-white rounded-full shadow-lg shadow-indigo-200
                    font-semibold text-base select-none
                  "
                  id="flow-start-node"
                >
                  <Play size={16} className="fill-white" />
                  {t('agents.startResearch')}
                </div>
              </div>

              <div className="flex items-center gap-3 mb-5 relative z-10">
                <div className="flex-1 border-t border-dashed border-indigo-200" />
                <span className="text-xs font-semibold text-indigo-400 uppercase tracking-widest whitespace-nowrap px-2">
                  {t('agents.parallelLabel')}
                </span>
                <div className="flex-1 border-t border-dashed border-indigo-200" />
              </div>

              <div
                className="grid gap-4 relative z-10 mb-10"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
              >
                {AGENT_DEFS.map((agent, i) => (
                  <button
                    key={agent.id}
                    ref={agentRefs.current[i]}
                    id={`agent-card-${agent.id}`}
                    onClick={() => openAgent(agent.workflowId)}
                    className={`
                      group relative flex flex-col items-center text-center
                      p-5 rounded-2xl border-2 border-transparent
                      bg-white shadow-sm
                      hover:shadow-xl hover:-translate-y-1
                      focus:outline-none focus:ring-2 ${agent.ring} focus:ring-offset-2
                      transition-all duration-200 cursor-pointer
                      hover:border-current
                    `}
                    style={{ minHeight: 130 }}
                    title={t('agents.openWorkflow', { name: agentName(agent.key) })}
                  >
                    <div
                      className={`
                        w-12 h-12 rounded-xl flex items-center justify-center mb-3
                        bg-gradient-to-br ${agent.color} text-white
                        shadow-md group-hover:scale-110 transition-transform duration-200
                      `}
                    >
                      {agent.icon}
                    </div>

                    <span
                      className={`
                        absolute top-3 left-3 w-5 h-5 rounded-full
                        flex items-center justify-center text-xs font-bold
                        ${agent.bg} ${agent.text}
                      `}
                    >
                      {agent.id}
                    </span>

                    <span
                      className="
                        absolute top-3 right-3
                        opacity-0 group-hover:opacity-100
                        transition-opacity duration-200
                        text-gray-400
                      "
                    >
                      <ExternalLink size={13} />
                    </span>

                    <p className="text-sm font-semibold text-gray-800 leading-tight mb-1">
                      {agentName(agent.key)}
                    </p>
                    <p className="text-xs text-gray-400 leading-snug">{agentDescription(agent.key)}</p>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 mb-5 relative z-10">
                <div className="flex-1 border-t border-dashed border-emerald-200" />
                <span className="text-xs font-semibold text-emerald-500 uppercase tracking-widest whitespace-nowrap px-2">
                  {t('agents.resultsMerged')}
                </span>
                <div className="flex-1 border-t border-dashed border-emerald-200" />
              </div>

              <div className="flex justify-center relative z-10">
                <div
                  ref={reportRef}
                  className="
                    flex items-center gap-3 px-8 py-4
                    bg-gradient-to-r from-emerald-600 to-emerald-500
                    text-white rounded-2xl shadow-lg shadow-emerald-200
                    font-semibold text-base select-none
                  "
                  id="flow-final-report-node"
                >
                  <FileBarChart2 size={20} />
                  {t('agents.finalReport')}
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-6 text-xs text-gray-400">
              <span className="flex items-center gap-2">
                <svg width="28" height="10">
                  <line x1="0" y1="5" x2="28" y2="5" stroke="#6366f1" strokeWidth="1.5" />
                </svg>
                {t('agents.legend.triggerFlow')}
              </span>
              <span className="flex items-center gap-2">
                <svg width="28" height="10">
                  <line x1="0" y1="5" x2="28" y2="5" stroke="#10b981" strokeWidth="1.5" strokeDasharray="4 3" />
                </svg>
                {t('agents.legend.resultsCollected')}
              </span>
              <span className="flex items-center gap-2">
                <ExternalLink size={12} />
                {t('agents.legend.clickCard')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsPage;
